import languages from "@/data/languages.json";
import { VOICE_DISPLAY_NAMES, type VoiceKey } from "@/data/voiceDisplayNames";

interface Languages {
  [key: string]: string;
}

const languagesData: Languages = languages;

export function getErrorTelemetry(error: unknown) {
  if (error instanceof Error) {
    return {
      error: error.message,
      error_name: error.name,
    };
  }

  return {
    error: String(error),
    error_name: null,
  };
}

export function getServerUrlHost(serverUrl?: string | null) {
  if (!serverUrl) return null;

  try {
    return new URL(serverUrl).host;
  } catch {
    return null;
  }
}

export function getAppVersionTelemetry() {
  return {
    app_version:
      process.env.NEXT_PUBLIC_APP_VERSION || process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA || null,
  };
}

export function getBrowserNetworkDiagnostics() {
  if (typeof navigator === "undefined") {
    return {
      network_effective_type: null,
      network_downlink: null,
      network_rtt: null,
      network_save_data: null,
    };
  }

  const connection = (
    navigator as Navigator & {
      connection?: {
        effectiveType?: string;
        downlink?: number;
        rtt?: number;
        saveData?: boolean;
      };
    }
  ).connection;

  return {
    network_effective_type: connection?.effectiveType || null,
    network_downlink: typeof connection?.downlink === "number" ? connection.downlink : null,
    network_rtt: typeof connection?.rtt === "number" ? connection.rtt : null,
    network_save_data: typeof connection?.saveData === "boolean" ? connection.saveData : null,
  };
}

export function getBrowserSessionDiagnostics() {
  if (typeof window === "undefined") {
    return {
      pathname: null,
      search: null,
      hash: null,
      hasSearch: null,
      hasHash: null,
      online: null,
      visibilityState: null,
      pageHidden: null,
      serviceWorkerControlled: null,
      nodeEnv: process.env.NODE_ENV,
    };
  }

  return {
    pathname: window.location.pathname,
    search: null,
    hash: null,
    hasSearch: Boolean(window.location.search),
    hasHash: Boolean(window.location.hash),
    online: window.navigator.onLine,
    visibilityState: typeof document !== "undefined" ? document.visibilityState : null,
    pageHidden: typeof document !== "undefined" ? document.hidden : null,
    serviceWorkerControlled: Boolean(window.navigator.serviceWorker?.controller),
    nodeEnv: process.env.NODE_ENV,
  };
}

export function getLanguageNameByCode(languageCode: string): string {
  return (
    Object.keys(languagesData).find((key) => languagesData[key] === languageCode) || languageCode
  );
}

export function getAvatarTelemetry({
  selectedVoice,
  avatarId,
  noAvatar,
}: {
  selectedVoice: string;
  avatarId?: string;
  noAvatar: boolean;
}) {
  const voiceKey = selectedVoice as VoiceKey;
  const displayName = VOICE_DISPLAY_NAMES[voiceKey] || selectedVoice || null;
  const shortName = displayName?.replace(/^(Mr\.|Mrs\.|Ms\.)\s+/i, "") || null;

  return {
    avatar_enabled: !noAvatar,
    avatar_id: noAvatar ? null : avatarId || null,
    avatar_name: noAvatar ? "none" : shortName,
    avatar_display_name: noAvatar ? "None" : displayName,
    avatar_voice: selectedVoice || null,
    tutor_name: noAvatar ? "none" : shortName,
  };
}
